import { useState, useEffect, useCallback } from "react";
import { Compass, BookOpen, Sparkles, CheckCircle2, Square, CheckSquare, Save, Loader2, BarChart3 } from "lucide-react";
import { apiCall } from "../App";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TIMEFRAMES = ["1 week", "2 weeks", "1 month", "3 months", "Ongoing"];

// ─────────── ALIGNMENT ───────────
export const AlignmentStage = ({ pillar, progress, isReadOnly, onChange }) => {
  return (
    <div className="space-y-6" data-testid="alignment-stage">
      {pillar.elements.map((el, idx) => {
        const entry = progress[el.code] || {};
        const al = el.alignment || { name: "", steps: [] };
        const chosen = entry.chosen_step;
        return (
          <div key={el.code} className="rounded-xl border border-divine-gold/15 bg-cosmic-deep/30 p-5" data-testid={`alignment-element-${el.code}`}>
            <div className="mb-3">
              <h3 className="text-divine-gold font-heading text-base">
                <span className="text-chakra-crown/40 text-sm mr-2">{idx + 1}.</span>
                {el.name} · <span className="italic text-chakra-crown/70">{el.sanskrit}</span>
              </h3>
              {al.name && <p className="text-purple-300/80 text-xs mt-1 font-heading">{al.name}</p>}
            </div>
            <p className="text-chakra-crown/65 text-[11px] uppercase tracking-wider mb-3">Choose ONE practice to begin</p>
            <div className="grid grid-cols-1 gap-2 mb-4">
              {al.steps.map((step, i) => {
                const val = i + 1;
                const sel = chosen === val;
                return (
                  <button
                    key={val}
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => onChange(el.code, { ...entry, chosen_step: val, started_at: entry.started_at || new Date().toISOString() })}
                    data-testid={`alignment-step-${el.code}-${val}`}
                    className={`text-left p-3 rounded-lg border text-xs transition-all flex items-start gap-3 ${
                      sel ? "border-purple-400 text-purple-100 bg-purple-500/20"
                          : "border-divine-gold/15 text-chakra-crown/65 hover:border-purple-500/40 hover:bg-purple-500/5"
                    } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-[10px] font-bold ${sel ? "border-purple-300 bg-purple-500/40" : "border-current"}`}>
                      {sel ? <CheckCircle2 className="w-3.5 h-3.5" /> : val}
                    </span>
                    <span className="leading-relaxed">{step}</span>
                  </button>
                );
              })}
            </div>
            {chosen && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-chakra-crown/50 block mb-1">Timeframe</label>
                  <select
                    value={entry.timeframe || ""}
                    disabled={isReadOnly}
                    onChange={(e) => onChange(el.code, { ...entry, timeframe: e.target.value })}
                    data-testid={`alignment-timeframe-${el.code}`}
                    className="w-full bg-cosmic-void/40 border border-divine-gold/15 text-chakra-crown text-xs rounded-lg px-2 py-2"
                  >
                    <option value="">— select —</option>
                    {TIMEFRAMES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-chakra-crown/50 block mb-1">Status</label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      type="button"
                      disabled={isReadOnly}
                      data-testid={`alignment-complete-${el.code}`}
                      onClick={() => onChange(el.code, { ...entry, completed_at: entry.completed_at ? null : new Date().toISOString() })}
                      className={entry.completed_at
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        : "bg-purple-600/30 border border-purple-500/40 text-purple-200 hover:bg-purple-500/40 text-xs"
                      }
                    >
                      {entry.completed_at ? "✓ Completed" : "Mark Complete"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-chakra-crown/50 block mb-1">Reflection / Tracking</label>
              <Textarea
                value={entry.notes || ""}
                disabled={isReadOnly}
                onChange={(e) => onChange(el.code, { ...entry, notes: e.target.value })}
                data-testid={`alignment-notes-${el.code}`}
                placeholder="Track what happened. What shifted? What resisted?"
                className="bg-cosmic-void/40 border-divine-gold/15 text-chakra-crown text-xs min-h-[60px]"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────── AWAKENING ───────────
export const AwakeningStage = ({ pillar, progress, isReadOnly, onChange }) => {
  return (
    <div className="space-y-6" data-testid="awakening-stage">
      {pillar.elements.map((el, idx) => {
        const entry = progress[el.code] || {};
        return (
          <div key={el.code} className="rounded-xl border border-divine-gold/15 bg-cosmic-deep/30 p-5" data-testid={`awakening-element-${el.code}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-divine-gold font-heading text-base">
                  <span className="text-chakra-crown/40 text-sm mr-2">{idx + 1}.</span>
                  {el.name} · <span className="italic text-chakra-crown/70">{el.sanskrit}</span>
                </h3>
                <p className="text-sky-300/70 text-[10px] uppercase tracking-wider mt-1">Wisdom Transmission</p>
              </div>
              <button
                type="button"
                disabled={isReadOnly}
                onClick={() => onChange(el.code, { ...entry, read_at: entry.read_at ? null : new Date().toISOString() })}
                data-testid={`awakening-read-${el.code}`}
                className={`shrink-0 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${
                  entry.read_at
                    ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/40"
                    : "text-chakra-crown/50 bg-cosmic-light/5 border-divine-gold/20 hover:border-divine-gold/40"
                }`}
              >
                {entry.read_at ? "✓ Read" : "Mark as Read"}
              </button>
            </div>
            <blockquote className="border-l-2 border-sky-500/40 pl-4 py-2 my-3 text-chakra-crown/85 text-sm leading-relaxed italic">
              {el.awakening}
            </blockquote>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-wider text-chakra-crown/50 block mb-1">Your Reflection</label>
              <Textarea
                value={entry.reflection || ""}
                disabled={isReadOnly}
                onChange={(e) => onChange(el.code, { ...entry, reflection: e.target.value })}
                data-testid={`awakening-reflection-${el.code}`}
                placeholder="What did this open in you? What changed when understanding preceded change?"
                className="bg-cosmic-void/40 border-divine-gold/15 text-chakra-crown text-xs min-h-[60px]"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────── HARMONY ───────────
export const HarmonyStage = ({ pillar, progress, isReadOnly, onChange }) => {
  return (
    <div className="space-y-6" data-testid="harmony-stage">
      {pillar.elements.map((el, idx) => {
        const entry = progress[el.code] || {};
        const items = entry.items && entry.items.length === 5 ? entry.items : [false, false, false, false, false];
        const checked = items.filter(Boolean).length;
        const verified = checked === 5;
        return (
          <div key={el.code} className={`rounded-xl border p-5 ${verified ? "border-emerald-500/40 bg-emerald-500/5" : "border-divine-gold/15 bg-cosmic-deep/30"}`} data-testid={`harmony-element-${el.code}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-divine-gold font-heading text-base">
                  <span className="text-chakra-crown/40 text-sm mr-2">{idx + 1}.</span>
                  {el.name} · <span className="italic text-chakra-crown/70">{el.sanskrit}</span>
                </h3>
                <p className="text-rose-300/70 text-[10px] uppercase tracking-wider mt-1">Harmony Verification · {checked}/5</p>
              </div>
              {verified && (
                <span className="shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border text-emerald-200 bg-emerald-500/25 border-emerald-400/60" data-testid={`harmony-verified-${el.code}`}>
                  ● VERIFIED
                </span>
              )}
            </div>
            <div className="space-y-2">
              {(el.harmony || []).map((item, i) => {
                const c = !!items[i];
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => {
                      const next = [...items];
                      next[i] = !c;
                      onChange(el.code, { ...entry, items: next, verified_at: next.every(Boolean) ? new Date().toISOString() : null });
                    }}
                    data-testid={`harmony-item-${el.code}-${i + 1}`}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border text-xs transition-all ${
                      c ? "border-emerald-500/40 text-emerald-200 bg-emerald-500/10"
                        : "border-divine-gold/15 text-chakra-crown/75 hover:border-emerald-500/30 hover:bg-emerald-500/5"
                    } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {c ? <CheckSquare className="w-4 h-4 shrink-0 mt-0.5 text-emerald-300" /> : <Square className="w-4 h-4 shrink-0 mt-0.5" />}
                    <span className="leading-relaxed flex-1">{item}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────── HOOK: progress data + save logic per stage ───────────
export const useStageData = ({ token, stage, doc, isReadOnly, isClient = true, userId = null }) => {
  const fieldMap = { alignment: "alignment_progress", awakening: "awakening_progress", harmony: "harmony_checklist" };
  const field = fieldMap[stage];
  const [data, setData] = useState((doc && doc[field]) || {});
  const [saving, setSaving] = useState(false);

  // Re-seed when doc changes — but only if the incoming value truly differs, to prevent
  // an infinite re-seed → auto-save → refetch → re-seed loop when the server returns the
  // same payload we just sent (React's `setData({})` creates a new object reference each
  // render, which would otherwise flip the debounced-save effect's dependency every cycle).
  useEffect(() => {
    const incoming = (doc && doc[field]) || {};
    setData(prev => {
      try {
        if (JSON.stringify(prev) === JSON.stringify(incoming)) return prev;
      } catch { /* fall through */ }
      return incoming;
    });
  }, [doc, field]);

  const handleChange = useCallback((code, entry) => {
    if (isReadOnly) return;
    setData(prev => ({ ...prev, [code]: entry }));
  }, [isReadOnly]);

  // debounced save
  useEffect(() => {
    if (!doc || isReadOnly) return;
    const t = setTimeout(async () => {
      setSaving(true);
      const url = isClient
        ? `/coach-assessment-matrix/me/${stage}`
        : `/coach-assessment-matrix/user/${userId}/${stage}/coach`;
      const payload = isClient
        ? { [field]: data }
        : { [stage === "alignment" ? "coach_alignment" : "coach_harmony"]: data };
      const r = await apiCall("PUT", url, payload, token);
      setSaving(false);
      if (!r.success) toast.error(r.error || "Auto-save failed");
    }, 1500);
    return () => clearTimeout(t);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, handleChange, saving };
};

export const STAGE_LIST = [
  { key: "awareness", label: "Awareness", icon: Compass, desc: "Diagnostic — Tamas → Sattva" },
  { key: "alignment", label: "Alignment", icon: Sparkles, desc: "Prescription — Choose Practice" },
  { key: "awakening", label: "Awakening", icon: BookOpen, desc: "Wisdom — Read & Reflect" },
  { key: "harmony", label: "Harmony", icon: CheckCircle2, desc: "Verification — Checklist" },
  { key: "summary", label: "Summary", icon: BarChart3, desc: "Radar · PDF · Suggestions" },
];
